import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  Plus,
  Megaphone,
  HelpCircle,
  Bell,
  LogOut,
  User,
  Settings,
  Clock,
  KeyRound
} from "lucide-react";
import { logoutUser } from "../api/auth";
import ChangePasswordDialog, { useChangePassword } from "./Changepassworddialog";

const Header = ({ title, icon }) => {
  const [profileOpen, setProfileOpen] = useState(false);
  const [userData, setUserData] = useState({ name: "User", email: "", department: "" });
  const [now, setNow] = useState(new Date());
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const { openChangePassword, changePasswordState, closeChangePassword } = useChangePassword();

  /* Keep the header clock ticking */
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  /* Load active user data from local storage on component mount */
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        setUserData(JSON.parse(storedUser));
      }
    } catch (err) {
      console.error("Failed to parse user session metrics:", err);
    }
  }, []);

  /* Close profile dropdown when clicking outside */
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setProfileOpen(false);
    try {
      // Routed through the shared `api` instance (see ./api) via auth.js,
      // so the same request interceptor that attaches the auth header
      // for leads/deals handles it here too.
      await logoutUser();
    } catch (err) {
      console.error("Async server session clearing trace failed:", err);
    } finally {
      // Clear everything this app has stored, not just the three known
      // auth keys - matches the inactivity auto-logout path in api.js
      // (forceLogoutAndExpireSession), which already does a full
      // localStorage.clear(). Keeps a manual logout and a timeout logout
      // leaving the browser in the same clean state.
      localStorage.clear();

      // Pivot worker interface frame target location context gracefully
      navigate("/");
    }
  };

  // Safe Fallback calculation for User Avatar Initial Letter
  const userInitial = userData.name ? userData.name.charAt(0).toUpperCase() : "U";

  return (
    <>
    <header className="fixed top-0 left-0 right-0 z-50 h-14 bg-white border-b border-[#E2E8F0] shadow-[0_1px_3px_rgba(15,23,42,0.04)] flex items-center px-2 sm:px-4 justify-between gap-2">
      {/* LEFT SECTION */}
      <div className="flex items-center gap-x-2 sm:gap-x-4 min-w-0">
        {/* Back Navigation Button */}
        <button
        title="Back"
          onClick={() => navigate(-1)}
          className="p-2 rounded hover:bg-gray-100 transition shrink-0"
        >
          <ArrowLeft className="w-5 h-5 text-[#1E293B]" />
        </button>

        {/* Brand System Logo */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-8 h-8 bg-gradient-to-br from-[#3B9CFF] to-[#007BFF] rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-md shadow-blue-200 transition-transform duration-200 hover:scale-105">
            F1
          </div>
          <div className="leading-tight hidden sm:block">
            <div className="text-xs font-semibold text-[#1E293B]">F1</div>
            <div className="text-sm text-gray-500">Infotech</div>
          </div>
        </div>

        {/* Structural Section Divider */}
        <div className="hidden sm:block h-8 w-[1px] bg-gradient-to-b from-transparent via-[#E2E8F0] to-transparent mx-2 shrink-0"></div>

        {/* Active Route Identifier Dashboard Label */}
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5 min-w-0">
          {icon}
          <span className="text-sm font-bold uppercase tracking-wide text-[#1E293B] truncate max-w-[100px] sm:max-w-none">
            {title}
          </span>
        </div>
      </div>

      {/* CENTER SECTION: Global CRM Search Bar */}
      <div className="flex-1 max-w-xl mx-2 sm:mx-6 min-w-0 hidden md:block">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search across opportunities and more..."
            className="w-full pl-9 pr-14 py-2 rounded-lg border border-[#E2E8F0] bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-[#007BFF] focus:shadow-sm text-sm transition-all duration-150"
          />
          <span className="hidden lg:flex items-center gap-0.5 absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-slate-400 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 pointer-events-none">
            Ctrl K
          </span>
        </div>
      </div>

      {/* Compact search trigger for narrow screens where the full bar is hidden */}
      <button
        className="md:hidden p-2 rounded hover:bg-gray-100 transition shrink-0"
        title="Search"
      >
        <Search className="w-5 h-5 text-[#1E293B]" />
      </button>

      {/* RIGHT SECTION: Quick Actions Bar */}
      <div className="flex items-center gap-x-2 sm:gap-x-4 shrink-0">
        {/* Rapid Capture Create Entity Action Button */}
        {/* <button className="w-9 h-9 bg-[#007BFF] hover:bg-blue-700 text-white rounded flex items-center justify-center transition">
          <Plus className="w-5 h-5" />
        </button> */}

        {/* Broadcast System Trigger */}
        {/* <button className="p-2 rounded hover:bg-gray-100 transition">
          <Megaphone className="w-5 h-5 text-[#1E293B]" />
        </button> */}

        {/* Documentation / Knowledge Support Center */}
        {/* <button className="p-2 rounded hover:bg-gray-100 transition">
          <HelpCircle className="w-5 h-5 text-[#1E293B]" />
        </button> */}

        {/* Realtime Application Feeds & Notifications Toggle */}
        {/* <div className="relative">
          <button className="p-2 rounded hover:bg-gray-100 transition">
            <Bell className="w-5 h-5 text-[#1E293B]" />
          </button>
          <span className="absolute -top-1 -right-1 bg-[#FBBF24] text-white text-[10px] px-1.5 rounded-full">
            1
          </span>
        </div> */}

        {/* Live date / time readout */}
        <div className="hidden md:flex items-center gap-2 select-none">
          <Clock className="w-3.5 h-3.5 text-slate-300" />
          <div className="flex flex-col items-end leading-tight">
            <span className="text-xs font-bold text-[#1E293B]">
              {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
            <span className="text-[10px] text-gray-400">
              {now.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
            </span>
          </div>
        </div>

        <div className="hidden md:block h-8 w-[1px] bg-gradient-to-b from-transparent via-[#E2E8F0] to-transparent"></div>

        {/* Inline greeting so the profile trigger doesn't sit alone in empty space */}
        <div className="hidden lg:flex flex-col items-end leading-tight select-none max-w-[140px]">
          <span className="text-xs font-bold text-[#1E293B] truncate w-full text-right">
            {userData.name}
          </span>
          <span className="text-[10px] text-gray-400 truncate w-full text-right">
            {userData.department || "Team Member"}
          </span>
        </div>

        {/* Profile Dropdown Container */}
        <div className="relative flex items-center" ref={profileRef}>
          {/* Interactive Trigger Button */}
          <button
            onClick={() => setProfileOpen(!profileOpen)}
            className="relative group flex items-center gap-x-2 focus:outline-none select-none p-1 rounded-lg hover:bg-slate-50 transition-all duration-200"
          >
            {/* Dynamic Avatar Initial letter logic applied */}
            <div className={`w-9 h-9 rounded-xl font-bold flex items-center justify-center transition-all duration-200 shadow-sm ${
              profileOpen 
                ? "bg-gradient-to-br from-[#3B9CFF] to-[#007BFF] text-white ring-4 ring-blue-100 shadow-md" 
                : "bg-blue-50 text-[#007BFF] group-hover:bg-gradient-to-br group-hover:from-[#3B9CFF] group-hover:to-[#007BFF] group-hover:text-white group-hover:shadow-md"
            }`}>
              {userInitial}
            </div>
            
            {/* Blinking Live System Status Pin Indicator */}
            <span className="absolute bottom-1 right-1 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500 border border-white"></span>
            </span>
          </button>

          {/* Action Popover Interface Container */}
          {profileOpen && (
            <div className="absolute right-0 top-full mt-2.5 w-56 max-w-[85vw] bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-1.5 origin-top-right animate-in fade-in slide-in-from-top-2 duration-150 ease-out">
              {/* Dynamic Logged-In User Information Panel */}
              <div className="mx-2 px-2.5 py-2.5 bg-slate-50 rounded-lg mb-1 flex flex-col gap-0.5">
                <div className="text-xs font-black text-slate-800 flex items-center gap-1.5 max-w-full">
                  <span className="truncate">{userData.name}</span>
                  {userData.department && (
                    <span className="bg-blue-100 text-[#007BFF] text-[9px] px-1.5 py-0.5 rounded-md font-bold uppercase tracking-wider shrink-0 max-w-[80px] truncate">
                      {userData.department}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 font-medium truncate">
                  {userData.email || "No Email Provided"}
                </div>
              </div>

              {/* Functional Feature Items Nav List */}
              <div className="px-1.5 space-y-0.5">
                {/* <button 
                  onClick={() => setProfileOpen(false)}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs font-semibold text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-lg text-left transition-all"
                >
                  <User size={14} className="text-slate-400" />
                  <span>My Profile</span>
                </button>

                <button 
                  onClick={() => setProfileOpen(false)}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs font-semibold text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-lg text-left transition-all"
                >
                  <Settings size={14} className="text-slate-400" />
                  <span>Account Settings</span>
                </button> */}
              </div>

              {/* Section Content Separator */}
              <div className="h-[1px] bg-slate-100 my-1.5 mx-1.5"></div>

              {/* Change Password */}
              <div className="px-1.5">
                <button
                  onClick={() => {
                    setProfileOpen(false);
                    openChangePassword();
                  }}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs font-semibold text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-lg text-left transition-all"
                >
                  <KeyRound size={14} className="text-slate-400" />
                  <span>Change Password</span>
                </button>
              </div>

              {/* Section Content Separator */}
              <div className="h-[1px] bg-slate-100 my-1.5 mx-1.5"></div>

              {/* Destructive Logout Control */}
              <div className="px-1.5">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs font-bold text-red-600 hover:bg-red-50 rounded-lg text-left transition-all"
                >
                  <LogOut size={14} className="text-red-500" />
                  <span>Log out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
    <ChangePasswordDialog changePasswordState={changePasswordState} onClose={closeChangePassword} />
    </>
  );
};

export default Header;